import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import procuracaoService, { Procuracao, ProcuracaoStatus } from '../services/procuracaoService';
import DashboardPagination from '../components/DashboardPagination';
import { UI_THEME } from '../ui/theme';
import { FIELD_CLASS } from '../components/ui/fieldStyles';
import { Table, Tbody, Td, Th, Thead, Tr } from '../components/ui/Table';

type Tab = 'concedidas' | 'recebidas';

const statusLabel: Record<ProcuracaoStatus, string> = {
  PENDENTE: 'Aguardando aceite',
  ATIVA: 'Ativa',
  RECUSADA: 'Recusada',
  REVOGADA: 'Revogada',
  RENUNCIADA: 'Renunciada',
  EXPIRADA: 'Expirada',
};

const statusClass: Record<ProcuracaoStatus, string> = {
  PENDENTE: 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/40 dark:bg-amber-950/30 dark:text-amber-300',
  ATIVA: 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-950/30 dark:text-emerald-300',
  RECUSADA: 'border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-400',
  REVOGADA: 'border-red-200 bg-red-50 text-red-700 dark:border-red-500/40 dark:bg-red-950/30 dark:text-red-300',
  RENUNCIADA: 'border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-400',
  EXPIRADA: 'border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-400',
};

const formatDocument = (value?: string) => {
  const digits = (value || '').replace(/\D/g, '');
  if (digits.length === 11) return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (digits.length === 14) return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  return value || '-';
};

const maskCpf = (value: string) => {
  const d = value.replace(/\D/g, '').slice(0, 11);
  if (d.length > 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
  if (d.length > 6) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  if (d.length > 3) return `${d.slice(0, 3)}.${d.slice(3)}`;
  return d;
};

const formatDate = (value?: string) => value
  ? new Intl.DateTimeFormat('pt-BR', { timeZone: 'UTC' }).format(new Date(`${value.slice(0, 10)}T00:00:00Z`))
  : '-';

const ProcuracoesPage: React.FC = () => {
  const isSaas = UI_THEME === 'saas';
  const [tab, setTab] = useState<Tab>('concedidas');
  const [concedidas, setConcedidas] = useState<Procuracao[]>([]);
  const [recebidas, setRecebidas] = useState<Procuracao[]>([]);
  const [podeConceder, setPodeConceder] = useState(false);
  const [visaoAdmin, setVisaoAdmin] = useState(false);
  const [cpf, setCpf] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [perfil, granted, received] = await Promise.all([
        procuracaoService.perfil(),
        procuracaoService.concedidas(),
        procuracaoService.recebidas(),
      ]);
      setPodeConceder(perfil.podeConceder);
      setVisaoAdmin(perfil.visaoAdmin);
      setConcedidas(granted);
      setRecebidas(received);
      if (perfil.visaoAdmin) setTab('concedidas');
      else if (!perfil.podeConceder && received.length > 0) setTab('recebidas');
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Não foi possível carregar as procurações.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { setPage(1); }, [tab]);

  const items = tab === 'concedidas' ? concedidas : recebidas;
  const paginated = useMemo(() => items.slice((page - 1) * pageSize, page * pageSize), [items, page]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await procuracaoService.criar(cpf, dataFim);
      setCpf('');
      setDataFim('');
      setSuccess('Procuração enviada. O procurador deverá aceitá-la em até 30 dias.');
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Não foi possível criar a procuração.');
    } finally {
      setSaving(false);
    }
  };

  const act = async (action: 'aceitar' | 'recusar' | 'revogar' | 'renunciar', id: string) => {
    setError('');
    setSuccess('');
    try {
      await procuracaoService[action](id);
      setSuccess('Procuração atualizada com sucesso.');
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Não foi possível atualizar a procuração.');
    }
  };

  const headerClass = isSaas
    ? 'rounded-3xl border border-teal-100 bg-gradient-to-br from-white via-teal-50 to-indigo-50 p-6 shadow-sm dark:border-teal-500/30 dark:from-slate-900 dark:via-teal-950/40 dark:to-indigo-950/40'
    : 'border-b-2 border-blue-900 bg-white pb-5 dark:border-blue-400 dark:bg-transparent';
  const primaryClass = isSaas ? 'bg-teal-600 hover:bg-teal-700' : 'bg-blue-900 hover:bg-blue-800';

  return (
    <div className="w-full space-y-6">
      <section className={headerClass}>
        <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${isSaas ? 'text-teal-700 dark:text-teal-300' : 'text-blue-900 dark:text-blue-300'}`}>Controle de acesso</p>
        <h1 className="mt-2 text-2xl font-bold text-slate-900 dark:text-slate-100">Procurações</h1>
        <p className="mt-2 max-w-3xl text-sm text-slate-600 dark:text-slate-400">
          {visaoAdmin
            ? 'Visão consolidada de todas as procurações concedidas entre usuários e instituições.'
            : 'Autorize outra pessoa a operar em nome da instituição ou gerencie as autorizações recebidas.'}
        </p>
      </section>

      {podeConceder && (
        <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <div className="mb-4">
            <h2 className="font-semibold text-slate-900 dark:text-slate-100">Nova procuração</h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Concede acesso integral ao cadastro, emendas, planos de trabalho e prestações da instituição. O procurador não precisa ter conta ainda — ao se cadastrar com esse CPF, ele verá a procuração para aceitar.</p>
          </div>
          <div className="grid gap-4 md:grid-cols-[1fr_220px_auto] md:items-end">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">CPF do procurador
              <input value={cpf} onChange={e => setCpf(maskCpf(e.target.value))} inputMode="numeric" required placeholder="000.000.000-00" className={`${FIELD_CLASS} mt-1`} />
            </label>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Válida até
              <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} required className={`${FIELD_CLASS} mt-1`} />
            </label>
            <button disabled={saving} className={`rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60 ${primaryClass}`}>
              {saving ? 'Enviando...' : 'Conceder procuração'}
            </button>
          </div>
        </form>
      )}

      {error && <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/40 dark:bg-red-950/30 dark:text-red-300">{error}</div>}
      {success && <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-950/30 dark:text-emerald-300">{success}</div>}

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <div className="flex border-b border-slate-200 px-4 pt-3 dark:border-slate-700">
          {visaoAdmin ? (
            <span className={`border-b-2 px-4 py-3 text-sm font-semibold ${isSaas ? 'border-teal-600 text-teal-700 dark:text-teal-300' : 'border-blue-900 text-blue-900 dark:border-blue-400 dark:text-blue-300'}`}>
              Todas ({concedidas.length})
            </span>
          ) : (
            (['concedidas', 'recebidas'] as Tab[]).map(value => (
              <button key={value} onClick={() => setTab(value)} className={`border-b-2 px-4 py-3 text-sm font-semibold ${tab === value ? (isSaas ? 'border-teal-600 text-teal-700 dark:text-teal-300' : 'border-blue-900 text-blue-900 dark:border-blue-400 dark:text-blue-300') : 'border-transparent text-slate-500 dark:text-slate-400'}`}>
                {value === 'concedidas' ? `Concedidas (${concedidas.length})` : `Recebidas (${recebidas.length})`}
              </button>
            ))
          )}
        </div>

        {loading ? <p className="p-8 text-center text-sm text-slate-500 dark:text-slate-400">Carregando procurações...</p> : items.length === 0 ? (
          <p className="p-8 text-center text-sm text-slate-500 dark:text-slate-400">{visaoAdmin ? 'Nenhuma procuração registrada.' : `Nenhuma procuração ${tab === 'concedidas' ? 'concedida' : 'recebida'}.`}</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <Thead>
                <Tr>
                  <Th>Instituição</Th>
                  {visaoAdmin && <Th>Outorgante</Th>}
                  <Th>{visaoAdmin ? 'Procurador' : (tab === 'concedidas' ? 'Procurador' : 'Outorgante')}</Th>
                  <Th>Validade</Th>
                  <Th>Situação</Th>
                  {!visaoAdmin && <Th align="right">Ações</Th>}
                </Tr>
              </Thead>
              <Tbody>
                {paginated.map(item => (
                  <Tr key={item.id}>
                    <Td><div className="font-medium text-slate-900 dark:text-slate-100">{item.instituicaoNome || item.instituicaoId}</div></Td>
                    {visaoAdmin && <Td><div className="font-medium text-slate-800 dark:text-slate-200">{item.outorganteNome}</div><div className="text-xs text-slate-500 dark:text-slate-400">{formatDocument(item.outorganteDocumento)}</div></Td>}
                    <Td><div className="font-medium text-slate-800 dark:text-slate-200">{visaoAdmin || tab === 'concedidas' ? item.procuradorNome : item.outorganteNome}</div><div className="text-xs text-slate-500 dark:text-slate-400">{formatDocument(visaoAdmin || tab === 'concedidas' ? item.procuradorDocumento : item.outorganteDocumento)}</div></Td>
                    <Td className="text-slate-600 dark:text-slate-400">{formatDate(item.dataInicio)} a {formatDate(item.dataFim)}</Td>
                    <Td><span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${statusClass[item.status]}`}>{statusLabel[item.status]}</span></Td>
                    {!visaoAdmin && <Td><div className="flex justify-end gap-2">
                      {tab === 'recebidas' && item.status === 'PENDENTE' && <><button onClick={() => act('aceitar', item.id)} className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">Aceitar</button><button onClick={() => act('recusar', item.id)} className="text-sm font-semibold text-red-700 dark:text-red-400">Recusar</button></>}
                      {tab === 'recebidas' && item.status === 'ATIVA' && <button onClick={() => act('renunciar', item.id)} className="text-sm font-semibold text-red-700 dark:text-red-400">Renunciar</button>}
                      {tab === 'concedidas' && (item.status === 'PENDENTE' || item.status === 'ATIVA') && <button onClick={() => act('revogar', item.id)} className="text-sm font-semibold text-red-700 dark:text-red-400">Revogar</button>}
                    </div></Td>}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </div>
        )}
        {items.length > pageSize && <DashboardPagination page={page} pageSize={pageSize} totalItems={items.length} onPageChange={setPage} itemLabel="procurações" />}
      </section>
    </div>
  );
};

export default ProcuracoesPage;
